/**
 * Reschedule an existing appointment using the SAME day-first picker as booking.
 * BookingPicker in reschedule mode PATCHes the appointment (in-place edit, no
 * cancel/rebook, no re-charge) and calls onClose when done.
 */

import type { Appointment } from '@fitplatform/shared';
import { useQuery } from '@tanstack/react-query';
import { Muted } from '@/components/ui';
import { api } from '@/lib/api';
import { BookingPicker } from './BookingPicker';

function fmtDate(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function RescheduleModal({ appt, onClose }: { appt: Appointment; onClose: () => void }) {
  const cost = useQuery({
    queryKey: ['booking-cost'],
    queryFn: () =>
      api.get<{ credit_cost_per_session: number; balance: number; free_reschedules?: number }>('/api/appointments/cost'),
  });
  const free = cost.data?.free_reschedules ?? 2;
  const left = Math.max(0, free - (appt.reschedule_count ?? 0));
  const price = cost.data?.credit_cost_per_session ?? 0;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 60, display: 'grid', placeItems: 'end center' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
          background: 'var(--ink-2)', borderTopLeftRadius: 16, borderTopRightRadius: 16,
          padding: '1.25rem', paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
          <h3 style={{ margin: 0 }}>Reschedule</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--cream-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <Muted>
          Currently {fmtDate(appt.date)}, {appt.start_time}–{appt.end_time}. Pick a new time below.
        </Muted>
        {cost.data && price > 0 && (
          <Muted style={{ display: 'block', marginTop: '0.35rem', color: left > 0 ? undefined : 'var(--gold)' }}>
            {left > 0
              ? `${left} free reschedule${left === 1 ? '' : 's'} left for this session.`
              : `You've used your ${free} free reschedules. Moving this session again costs ${price} credit${price === 1 ? '' : 's'}.`}
          </Muted>
        )}

        <div style={{ marginTop: '1rem' }}>
          <BookingPicker rescheduleApptId={appt.id} onDone={onClose} />
        </div>
      </div>
    </div>
  );
}
